import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { PRIORITY_CONDITION_VALUES } from 'dashboard/constants/automation';
import {
  CONVERSATION_STAGE_LABELS,
  formatConversationStageLabel,
  getConversationStageOrder,
  isConversationStageLabel,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';

/**
 * Composable for handling macro-related functionality
 * @returns {Object} An object containing the getMacroDropdownValues function
 */
export const useMacros = () => {
  const { t } = useI18n();
  const getters = useStoreGetters();

  const labels = computed(() => getters['labels/getLabels'].value);
  const teams = computed(() => getters['teams/getTeams'].value);
  const agents = computed(() => getters['agents/getVerifiedAgents'].value);
  const stageOptions = computed(() => {
    const savedStages = labels.value
      .filter(label => isConversationStageLabel(label))
      .map(label => normalizeConversationStageLabel(label.title));
    const stages = savedStages.length ? savedStages : CONVERSATION_STAGE_LABELS;

    return [...new Set(stages)]
      .sort((a, b) => getConversationStageOrder(a) - getConversationStageOrder(b))
      .map(stage => ({
        id: stage,
        name: formatConversationStageLabel(stage),
      }));
  });

  const withNoneOption = options => [
    { id: 'nil', name: t('AUTOMATION.NONE_OPTION') },
    ...(options || []),
  ];

  /**
   * Get dropdown values based on the specified type
   * @param {string} type - The type of dropdown values to retrieve
   * @returns {Array} An array of dropdown values
   */
  const getMacroDropdownValues = type => {
    switch (type) {
      case 'assign_team':
        return withNoneOption(teams.value);
      case 'send_email_to_team':
        return teams.value;
      case 'assign_agent':
        return [
          ...withNoneOption(),
          { id: 'self', name: 'Self' },
          ...agents.value,
        ];
      case 'add_label':
      case 'remove_label':
        return labels.value.map(i => ({
          id: i.title,
          name: i.title,
        }));
      case 'change_priority':
        return PRIORITY_CONDITION_VALUES.map(item => ({
          id: item.id,
          name: t(`MACROS.PRIORITY_TYPES.${item.i18nKey}`),
        }));
      case 'change_stage':
        return stageOptions.value;
      default:
        return [];
    }
  };

  return {
    getMacroDropdownValues,
  };
};
